// A retainer in the client card's work tree: the whole picture of one engagement.
//
// Opening it shows, in this order:
//   1. THE SIGNED AGREEMENT — first, because the contract defines the engagement the
//      work sits under. Not itself a fold: it is usually a single row, and a second
//      click to reach the contract would defeat nesting it here at all.
//   2. ITS JOBS — each folding down to its own coverage.
//
// The collapsed header carries everything ABOUT the retainer — status, default issuer,
// topic count, and whether an agreement exists at all — so closing the fold never hides
// a fact, only contents. "אין הסכם" is the badge worth having: an engagement running
// without a signed contract on file is exactly what a partner would want to notice
// while scanning, and it is invisible if the only way to learn it is to open the fold.
//
// No money here. A retainer's P&L is on its own screen; a second set of figures on the
// client card would be a second place to read them and a second place for them to
// disagree.
import { useNavigate } from 'react-router-dom';
import { agreementApis } from '../../../api';
import he from '../../../i18n/he';
import { issuerName } from '../../../utils/issuer';
import Collapsible from '../../../components/Collapsible';
import StatusBadge, { isJobDone } from '../../../components/StatusBadge';
import Button from '../../../components/Button';
import AgreementsBlock from '../../../components/AgreementsBlock';
import JobBlock from './JobBlock';
import { isProject, engagementPath } from '../../../components/engagement';

export default function RetainerBlock({
  retainer, jobs, showDone, agreements, archived, publicationsFor, workFilesFor, onWorkFilesChange,
  open, onToggle, isJobOpen, onToggleJob,
  onChangeAgreements, onEditPublication, onDeletePublication,
}) {
  const navigate = useNavigate();
  // The card's toggle decides which jobs are LISTED. The badge below still counts every
  // job under this retainer, finished ones included — a collapsed header that changed
  // its count with the tab would be reporting the view instead of the engagement, which
  // is the one thing this header exists not to do.
  const shown = jobs.filter((j) => isJobDone(j.status) === showDone);
  // A retainer's own status is independent of its jobs' — a live engagement can have
  // every job finished, and a closed one can still have work outstanding. Neither is
  // derived from the other anywhere.

  return (
    <Collapsible
      level={1}
      open={open}
      onToggle={onToggle}
      title={retainer.title}
      badges={
        <>
          <StatusBadge kind="retainer" value={retainer.status} />
          <span className="badge gray">{jobs.length} {he.clientCard.topicsInRetainer}</span>
          {/* Counted from the LIVE agreements only. A retainer whose only contract has
              been archived genuinely has none on file, and saying otherwise while the
              archive view is off would be a badge that contradicts the list beneath
              it. */}
          <span className={`badge ${agreements.length > 0 ? 'green' : 'amber'}`}>
            {agreements.length > 0 ? he.clientCard.hasAgreement : he.clientCard.noAgreement}
          </span>
        </>
      }
      meta={
        <>
          <span className="muted" style={{ fontSize: 13 }}>
            {he.jobs.defaultIssuerShort}: {issuerName(retainer.issuing_partner_name)}
          </span>
          <Button small variant="subtle" onClick={() => navigate(engagementPath(retainer))}>
            {isProject(retainer) ? he.projects.openProject : he.clientCard.openRetainer}
          </Button>
        </>
      }
    >
      <AgreementsBlock
        agreements={agreements}
        archived={archived}
        ownerId={retainer.id}
        api={agreementApis.retainer}
        onChange={onChangeAgreements}
        heading={he.agreements.panelTitle}
      />

      <h3>{he.retainers.topics}</h3>
      {shown.length > 0
        ? shown.map((job) => (
          <JobBlock
            key={job.id}
            job={job}
            publications={publicationsFor(job.id)}
            workFiles={workFilesFor ? workFilesFor(job.id) : []}
            onWorkFilesChange={onWorkFilesChange}
            open={isJobOpen(job.id)}
            onToggle={() => onToggleJob(job.id)}
            onEditPublication={onEditPublication}
            onDeletePublication={onDeletePublication}
          />
        ))
        : <p className="muted">{showDone ? he.empty.doneTopics : he.empty.retainerTopics}</p>}
    </Collapsible>
  );
}
